import { request } from '@/utils/http'

// ==================== 消息模板相关模型 ====================

/** 消息模板VO模型（对应 BaseMessageTemplate） */
export interface MessageTemplateVO {
  /** 主键ID */
  id?: string | null
  /** 租户ID */
  tenantId?: string | null
  /** 创建人 */
  createdBy?: string | null
  /** 创建时间 */
  dateCreated?: string | null
  /** 修改人 */
  modifiedBy?: string | null
  /** 修改时间 */
  dateModified?: string | null
  /** 版本号 */
  version?: number | null
  /** 逻辑删除标识：0-未删除，1-已删除 */
  deleted?: number | null
  /** 通道类型: 1-短信 2-邮件 3-站内信 4-企微 */
  channelType?: number | null
  /** 模板编码（唯一标识） */
  templateCode?: string | null
  /** 模板名称 */
  templateName?: string | null
  /** 模板描述 */
  templateDescription?: string | null
  /** 邮件场景（邮件通道专用，与 BaseMessageTemplate.emailScenario 对应） */
  emailScenario?: string | null
  /** 触发操作（业务标识） */
  triggerAction?: string | null
  /** 通道配置代码（关联通道配置） */
  channelConfigCode?: string | null
  /** 模板类型: 1-纯文本, 2-文本卡片, 3-图文消息, 4-文件 */
  templateType?: number | null
  /** 状态: 1-启用(ENABLED), 0-停用(DISABLED) */
  status?: number | null
  /** 通用参数JSON */
  commonParams?: Record<string, any> | null
  /** 模板参数JSON */
  templateParams?: Record<string, any> | null
}

/** 消息模板查询请求参数 */
export interface MessageTemplateQueryRequest {
  /** 当前页码，默认为1 */
  pageNum?: number | null
  /** 每页大小，默认为10 */
  pageSize?: number | null
  /** 通道类型: 1-短信 2-邮件 3-站内信 4-企微 */
  channelType?: number | null
  /** 模板编码（模糊查询） */
  templateCode?: string | null
  /** 触发操作（模糊查询） */
  triggerAction?: string | null
  /** 模板名称（模糊查询） */
  templateName?: string | null
  /** 模板描述（模糊查询） */
  templateDescription?: string | null
  /** 模板类型: 1-纯文本, 2-文本卡片, 3-图文消息, 4-文件 */
  templateType?: number | null
  /** 状态: 1-启用(ENABLED), 0-停用(DISABLED) */
  status?: number | null
  /** 创建人ID（模糊查询） */
  createdBy?: string | null
}

/** 消息模板保存请求参数 */
export interface MessageTemplateSaveRequest {
  /** 主键ID（更新时必填，新增时为null。前端使用字符串类型，后端会自动转换为Long） */
  id?: string | null
  /** 通道类型: 1-短信 2-邮件 3-站内信 4-企微 */
  channelType: number
  /** 模板编码（新增时可为null，由后端生成） */
  templateCode?: string | null
  /** 模板名称 */
  templateName: string
  /** 模板描述 */
  templateDescription?: string | null
  /** 邮件场景（邮件通道专用） */
  emailScenario?: string | null
  /** 触发操作 */
  triggerAction?: string | null
  /** 通道配置代码（关联通道配置） */
  channelConfigCode?: string | null
  /** 模板类型: 1-纯文本, 2-文本卡片, 3-图文消息, 4-文件 */
  templateType?: number | null
  /** 状态: 1-启用(ENABLED), 0-停用(DISABLED) */
  status?: number | null
  /** 通用参数JSON */
  commonParams?: Record<string, any> | null
  /** 模板参数JSON */
  templateParams: Record<string, any>
}

/** 分页响应结构 */
export interface PageResponse<T> {
  records: T[]
  total: number
  current: number
  size: number
}

// ==================== 消息模板相关API ====================

const API_BASE = '/universal/base/message'

/** 分页查询消息模板列表 */
export const pageMessageTemplate = (params: MessageTemplateQueryRequest) => {
  return request.post<PageResponse<MessageTemplateVO>>(`${API_BASE}/template/page`, params)
}

/** 保存消息模板（新增或更新） */
export const saveMessageTemplate = (params: MessageTemplateSaveRequest) => {
  return request.post<number>(`${API_BASE}/template/save`, params)
}

/** 删除消息模板 */
export const deleteMessageTemplate = (id: string) => {
  return request.post<boolean>(`${API_BASE}/template/delete`, { id })
}

/** 根据ID获取消息模板详情 */
export const getMessageTemplateById = (id: string) => {
  return request.post<MessageTemplateVO>(`${API_BASE}/template/get`, { id })
}

/** 根据通道类型和触发操作获取启用模板 */
export const getMessageTemplateByTrigger = (channelType: number, triggerAction: string) => {
  return request.get<MessageTemplateVO>(`${API_BASE}/template/byTrigger/${channelType}/${triggerAction}`)
}

// ==================== 触发操作相关模型 ====================

/** 触发操作VO模型 */
export interface MessageTriggerActionVO {
  /** 主键ID */
  id?: string | null
  /** 触发操作代码（唯一标识） */
  actionCode?: string | null
  /** 触发操作名称 */
  actionName?: string | null
  /** 操作描述 */
  actionDescription?: string | null
  /** 所属模块 */
  moduleName?: string | null
  /** 业务类型 */
  businessType?: string | null
  /** 状态: 1-启用(ENABLED), 0-停用(DISABLED) */
  status?: number | null
  /** 占位符定义JSON（定义该操作可用的占位符变量及类型） */
  placeholderSchema?: Record<string, any> | null
}

// ==================== 触发操作相关API ====================

/** 查询触发操作列表 */
export const listMessageTriggerAction = (moduleName?: string, businessType?: string) => {
  const params = new URLSearchParams()
  if (moduleName) params.append('moduleName', moduleName)
  if (businessType) params.append('businessType', businessType)
  const query = params.toString()
  return request.post<MessageTriggerActionVO[]>(`${API_BASE}/triggerAction/list${query ? `?${query}` : ''}`)
}

/** 根据操作代码获取触发操作 */
export const getMessageTriggerActionByActionCode = (actionCode: string) => {
  return request.get<MessageTriggerActionVO>(`${API_BASE}/triggerAction/byActionCode/${actionCode}`)
}

// ==================== 消息发送相关接口 ====================

/** 消息发送请求参数 */
export interface MessageSendRequest {
  /** 模板ID */
  templateId: string
  /** 接收人（多个用逗号分隔） */
  receiver: string
  /** 占位符变量值（用于渲染模板） */
  variables?: Record<string, any> | null
}

/** 发送消息（测试发送） */
export const sendMessage = (params: MessageSendRequest) => {
  return request.post<boolean>(`${API_BASE}/message/send`, params)
}


